// Minimal Standard MIDI File writer — enough for Noteflight to open the piece
// as notation: one piano track, a tempo, a 4/4 time signature, a key signature
// and the notes (with rests where measured mode pads the bars).
const TPQ = 480; // ticks per quarter note
const LETTER_TO_SEMITONE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// Sharps/flats for a major key, as MIDI's key-signature meta event wants it.
const KEY_SIGNATURES = {
  C: 0, G: 1, D: 2, A: 3, E: 4, B: 5, "F#": 6, "C#": 7,
  F: -1, Bb: -2, Eb: -3, Ab: -4, Db: -5, Gb: -6, Cb: -7,
};

export function pitchToMidi(pitch) {
  const m = /^([A-G])(#{1,2}|b{1,2})?(-?\d+)$/.exec(String(pitch || "").trim());
  if (!m) return null;
  const [, letter, acc = "", octave] = m;
  let semis = LETTER_TO_SEMITONE[letter];
  for (const ch of acc) semis += ch === "#" ? 1 : -1;
  return (Number(octave) + 1) * 12 + semis;
}

const bytes = (...v) => v.flat();

function varLen(value) {
  let v = Math.max(0, Math.round(value));
  const out = [v & 0x7f];
  v >>= 7;
  while (v > 0) {
    out.unshift((v & 0x7f) | 0x80);
    v >>= 7;
  }
  return out;
}

const u16 = (v) => [(v >> 8) & 0xff, v & 0xff];
const u32 = (v) => [(v >> 24) & 0xff, (v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];

function textEvent(type, str) {
  const data = Array.from(new TextEncoder().encode(str));
  return bytes(varLen(0), 0xff, type, varLen(data.length), data);
}

function chunk(id, data) {
  return bytes(Array.from(new TextEncoder().encode(id)), u32(data.length), data);
}

/**
 * @param {object} o
 * @param {Array}  o.steps      measured: steps from measureSequence(); free: the play sequence
 * @param {number} o.stepMs     how long one step lasts in playback
 * @param {boolean} o.measured  true = every step is an eighth, rests included
 * @param {string} o.title      track/sequence name
 * @param {string} o.root       key root, for the key signature
 * @returns {Blob} a .mid file
 */
export function buildMidiFile({ steps, stepMs, measured, title = "Alephonics", root = "C" }) {
  // Measured: a step is an eighth, so a quarter is two steps. Free: keep a
  // steady quarter and give each note the length it was actually played for.
  const usPerQuarter = Math.round((measured ? stepMs * 2 : 500) * 1000);
  const ticksPerStep = measured
    ? TPQ / 2
    : Math.max(1, Math.round((TPQ * stepMs) / 500));

  const events = [];
  events.push(textEvent(0x03, title));
  events.push(bytes(varLen(0), 0xff, 0x51, varLen(3), [
    (usPerQuarter >> 16) & 0xff,
    (usPerQuarter >> 8) & 0xff,
    usPerQuarter & 0xff,
  ]));
  events.push(bytes(varLen(0), 0xff, 0x58, varLen(4), [4, 2, 24, 8])); // 4/4
  const sf = KEY_SIGNATURES[root] ?? 0;
  events.push(bytes(varLen(0), 0xff, 0x59, varLen(2), [sf & 0xff, 0]));
  events.push(bytes(varLen(0), 0xc0, 0)); // acoustic grand piano

  let pendingRestTicks = 0;
  steps.forEach((step) => {
    if (step.rest) {
      pendingRestTicks += ticksPerStep;
      return;
    }
    const midi = pitchToMidi(step.pitch);
    if (midi == null) {
      pendingRestTicks += ticksPerStep;
      return;
    }
    const velocity = Math.max(1, Math.min(127, Math.round((step.velocity || 0.8) * 110)));
    events.push(bytes(varLen(pendingRestTicks), 0x90, midi & 0x7f, velocity));
    events.push(bytes(varLen(ticksPerStep), 0x80, midi & 0x7f, 0));
    pendingRestTicks = 0;
  });
  // Trailing rests still count, so the final bar is complete in the notation.
  events.push(bytes(varLen(pendingRestTicks), 0xff, 0x2f, varLen(0)));

  const track = chunk("MTrk", events.flat());
  const header = chunk("MThd", bytes(u16(0), u16(1), u16(TPQ)));
  return new Blob([new Uint8Array(bytes(header, track))], { type: "audio/midi" });
}

export function downloadMidi(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".mid") ? filename : `${filename}.mid`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
